import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
	DocumentGetDecisionRoot,
	DocumentListFolder,
	DocumentUploadFile,
	DocumentDeleteFolder,
	DocumentDownloadFile,
} from 'services/documentService';
import { Modal, Button, Table, Upload, message, Space, Tabs } from 'antd';
import {
	FileOutlined,
	DeleteOutlined,
	UploadOutlined,
	DownloadOutlined,
	QuestionCircleOutlined,
} from '@ant-design/icons';
import ErrorModal from 'popup/ErrorModal';
import { useConfirm } from 'popup/ConfirmContext';
import { DocumentItem } from 'models/model';

const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';

const DECISION_TEMPLATES = [
	{ key: '0', name: '0.BaoCaoDangKyKhoaDaoTao.docx', label: '0. Báo cáo đăng ký khóa đào tạo' },
	{ key: '1', name: '1.QuyetDinhMoLop.docx', label: '1. Quyết định mở lớp' },
	{ key: '2', name: '2.QuyetDinhThanhLapHDXetTN.docx', label: '2. Quyết định thành lập HĐ xét TN' },
	{ key: '3', name: '3.QuyetDinhThanhLapHDThiTN.docx', label: '3. Quyết định thành lập HĐ thi TN' },
	{ key: '4', name: '4.QuyetDinhCongNhanTN.docx', label: '4. Quyết định công nhận TN' },
	{ key: '5', name: '5.PhanCongChamThi.docx', label: '5. Phân công chấm thi' },
	{ key: '6', name: '6.BienBanTongHopTN.docx', label: '6. Biên bản tổng hợp TN' },
];

const GUIDE_DATA = {
	reportInfo: [
		{
			variable: 'SoBaoCao',
			description: 'Số báo cáo',
			example: '12/BC-2026',
		},
		{
			variable: 'NgayBaoCao',
			description: 'Ngày lập báo cáo',
			example: 'ngày 15 tháng 01 năm 2026',
		},
	],
	unitInfo: [
		{
			variable: 'TenDV',
			description: 'Tên đơn vị viết tắt',
			example: 'TT GDNN ĐT & SHLX',
		},
		{
			variable: 'TenDonVi',
			description: 'Tên đầy đủ đơn vị',
			example:
				'Trung Tâm GDNN Đào Tạo & Sát Hạch Lái Xe Cơ Giới Đường Bộ',
		},
		{
			variable: 'TenKV',
			description: 'Tên trường/khu vực quản lý',
			example: 'TRƯỜNG CAO ĐẲNG TÂY ĐÔ',
		},
		{
			variable: 'PhoHieuTruong',
			description: 'Phó Hiệu trưởng',
			example: 'Trần Thị Vinh',
		},
		{ variable: 'ThuKy', description: 'Thư ký', example: 'Đỗ Thùy Trinh' },
		{
			variable: 'ToTruong',
			description: 'Tổ trưởng',
			example: 'Đỗ Thùy Trinh',
		},
		{
			variable: 'ChuTich',
			description: 'Chủ tịch hội đồng',
			example: 'Trần Thị Vinh',
		},
		{
			variable: 'GiamDoc',
			description: 'Giám đốc đơn vị',
			example: 'Phùng Chí Thành',
		},
	],
	courseInfo: [
		{ variable: 'TenKhoa', description: 'Tên khóa học', example: 'B2K03' },
		{
			variable: 'MaKhoa',
			description: 'Mã lớp/khóa',
			example: '95001.....',
		},
		{
			variable: 'TenHang',
			description: 'Hạng GPLX đào tạo',
			example: 'B2',
		},
		{ variable: 'STTKhoa', description: 'Số thứ tự khóa', example: '03' },
		{
			variable: 'SoLuongHV',
			description: 'Số lượng học viên',
			example: '35',
		},
		{
			variable: 'SLHV',
			description: 'Số lượng học viên tương ứng',
			example: '35',
		},
		{
			variable: 'SLHVKT',
			description: 'Số lượng học viên khóa trước',
			example: '35',
		},
	],
	dateInfo: [
		{
			variable: 'TGKhaiGiang',
			description: 'Ngày khai giảng (dd/MM/yyyy)',
			example: '05/01/2026',
		},
		{
			variable: 'TGBeGiang',
			description: 'Ngày bế giảng (dd/MM/yyyy)',
			example: '25/02/2026',
		},
		{
			variable: 'NgayKhaiGiang',
			description: 'Ngày khai giảng',
			example: '05',
		},
		{
			variable: 'ThangKhaiGiang',
			description: 'Tháng khai giảng',
			example: '01',
		},
		{
			variable: 'NamKhaiGiang',
			description: 'Năm khai giảng',
			example: '2026',
		},
		{
			variable: 'ThoiGianDT',
			description: 'Tổng thời gian đào tạo (ngày)',
			example: '52',
		},
		{
			variable: 'NgayThi',
			description: 'Ngày kiểm tra/sát hạch',
			example: '26/02/2026',
		},
	],
	decisionInfo: [
		{
			variable: 'SoQuyetDinh1',
			description: 'Số quyết định mở lớp',
			example: '123/QĐ-CĐTD',
		},
		{
			variable: 'SoQuyetDinh2',
			description: 'Số quyết định thành lập HĐ xét TN',
			example: '124/QĐ-CĐTD',
		},
		{
			variable: 'SoQuyetDinh3',
			description: 'Số quyết định thành lập HĐ thi TN',
			example: '125/QĐ-CĐTD',
		},
		{
			variable: 'SoQuyetDinh4',
			description: 'Số quyết định công nhận TN',
			example: '126/QĐ-CĐTD',
		},
		{
			variable: 'NgayQuyetDinh1',
			description: 'Ngày ban hành QĐ mở lớp',
			example: 'ngày 10 tháng 01 năm 2026',
		},
		{
			variable: 'NgayQuyetDinh2',
			description: 'Ngày ban hành QĐ thành lập HĐ xét TN',
			example: 'ngày 12 tháng 01 năm 2026',
		},
		{
			variable: 'NgayQuyetDinh3',
			description: 'Ngày ban hành QĐ thành lập HĐ thi TN',
			example: 'ngày 15 tháng 01 năm 2026',
		},
		{
			variable: 'NgayQuyetDinh4',
			description: 'Ngày ban hành QĐ công nhận TN',
			example: 'ngày 18 tháng 01 năm 2026',
		},
		{
			variable: 'NgayPhanCong',
			description: 'Ngày phân công nhiệm vụ',
			example: 'ngày 20 tháng 01 năm 2026',
		},
		{
			variable: 'NgayBienBanTH',
			description: 'Ngày lập biên bản tổng hợp',
			example: 'ngày 25 tháng 02 năm 2026',
		},
	],
};

export default function DecisionsPage() {
	const { confirm } = useConfirm();
	const [documents, setDocuments] = useState<DocumentItem[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [errorModalOpen, setErrorModalOpen] = useState(false);
	const [uploadModalVisible, setUploadModalVisible] = useState(false);
	const [guideModalVisible, setGuideModalVisible] = useState(false);
	const [rootFolder, setRootFolder] = useState<string | null>(null);

	// Load decisions data
	useEffect(() => {
		loadDecisions();
	}, []);

	const loadDecisions = async () => {
		try {
			setLoading(true);
			const response = await DocumentGetDecisionRoot();

			if (response.result) {
				const rootFolder = response.result;
				setRootFolder(rootFolder.url);
				const listResponse = await DocumentListFolder({
					courseId: GUID_EMPTY,
					path: rootFolder.url,
				});

				if (listResponse.result) {
					setDocuments(listResponse.result);
				}
			}
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi tải danh sách quyết định';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setLoading(false);
		}
	};

	const handleDownload = async (item: any) => {
		try {
			setLoading(true);
			const blob = await DocumentDownloadFile({
				filePath: item.url,
			});

			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = item.name;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi tải xuống';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setLoading(false);
		}
	};

	const handleUpload = async (options: any, templateName: string) => {
		const { file, onSuccess, onError } = options;

		try {
			const response = await DocumentUploadFile({
				courseId: GUID_EMPTY,
				name: templateName,
				targetPath: rootFolder,
				file,
			});

			if (response.result) {
				onSuccess(response, file);
				message.success(`Tải lên ${templateName} thành công`);
				await loadDecisions();
				setUploadModalVisible(false);
			} else {
				onError(new Error(response.message || 'Tải lên thất bại'));
			}
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi tải lên';
			setErrorMessage(errorMsg);
			setUploadModalVisible(false);
			setErrorModalOpen(true);
		}
	};

	const handleDelete = async (item: DocumentItem) => {
		const isConfirmed = await confirm({
			title: 'Xác nhận xóa',
			description: `Bạn có chắc chắn muốn xóa "${item.name}"?`,
		});

		if (!isConfirmed) return;

		try {
			setLoading(true);
			const response = await DocumentDeleteFolder({
				courseId: GUID_EMPTY,
				itemPath: item.url,
			});

			if (response.result) {
				toast.success('Xóa quyết định thành công');
				await loadDecisions();
			}
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi xóa quyết định';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			title: 'Tên quyết định',
			dataIndex: 'name',
			key: 'name',
			render: (text: string) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<FileOutlined style={{ fontSize: 18 }} />
					<span style={{ marginLeft: 8 }}>{text}</span>
				</div>
			),
		},
		{
			title: 'Ngày sửa',
			dataIndex: 'updateDate',
			key: 'updateDate',
			width: 200,
			render: (date: string) => new Date(date).toLocaleString(),
		},
		{
			title: 'Thao tác',
			key: 'action',
			width: 100,
			render: (_: any, record: DocumentItem) => (
				<Space>
					<Button
						type='text'
						icon={<DownloadOutlined />}
						onClick={() => handleDownload(record)}
					/>
					<Button
						type='text'
						icon={<DeleteOutlined />}
						danger
						onClick={() => handleDelete(record)}
					/>
				</Space>
			),
		},
	];

	return (
		<div className='p-6'>
			<div className='mb-4'>
				<h1 className='text-2xl font-bold text-gray-800 mb-2'>Quản lý Quyết định</h1>
				<p className='text-gray-600'>Quản lý các mẫu quyết định và tài liệu liên quan</p>
			</div>

			<div className='flex justify-end mb-4 gap-2'>
				<Button
					icon={<QuestionCircleOutlined />}
					onClick={() => setGuideModalVisible(true)}>
					Hướng dẫn
				</Button>
				<Button
					type='primary'
					onClick={() => setUploadModalVisible(true)}
					icon={<UploadOutlined />}>
					Tải quyết định lên
				</Button>
			</div>

			<Table
				columns={columns}
				dataSource={documents}
				rowKey='url'
				loading={loading}
				pagination={false}
				locale={{ emptyText: 'Chưa có quyết định nào được tải lên.' }}
			/>

			{/* Upload Modal */}
			<Modal
				title='Tải mẫu quyết định lên'
				open={uploadModalVisible}
				onCancel={() => setUploadModalVisible(false)}
				footer={null}
				centered
				width={600}>
				<div className='flex flex-col gap-3 py-5'>
					{DECISION_TEMPLATES.map((template) => (
						<Upload
							key={template.key}
							customRequest={(options) => handleUpload(options, template.name)}
							showUploadList={false}
							accept='.doc,.docx'>
							<Button type='primary' block size='large' className='text-left'>
								<div className='flex items-center justify-between'>
									<span>{template.label}</span>
								</div>
							</Button>
						</Upload>
					))}
				</div>
			</Modal>

			{/* Guide Modal */}
			<Modal
				title='Hướng dẫn sử dụng - Định dạng DOCX'
				open={guideModalVisible}
				onCancel={() => setGuideModalVisible(false)}
				footer={null}
				centered
				width={900}>
				<div className='py-4'>
					<p className='mb-4 text-gray-600'>
						Các biến sau đây sẽ được thay thế tự động trong tệp .docx khi xuất quyết định:
					</p>
					<Tabs
						defaultActiveKey='1'
						items={[
							{
								key: '1',
								label: 'Thông tin báo cáo',
								children: (
									<Table
										dataSource={GUIDE_DATA.reportInfo}
										pagination={false}
										size='small'
										columns={[
											{
												title: 'Tên biến',
												dataIndex: 'variable',
												key: 'variable',
												render: (text: string) => <code className='bg-gray-100 px-2 py-1 rounded'>{text}</code>,
											},
											{
												title: 'Diễn giải',
												dataIndex: 'description',
												key: 'description',
											},
											{
												title: 'Ví dụ',
												dataIndex: 'example',
												key: 'example',
												render: (text: string) => <span className='text-blue-600'>{text}</span>,
											},
										]}
									/>
								),
							},
							{
								key: '2',
								label: 'Thông tin đơn vị',
								children: (
									<Table
										dataSource={GUIDE_DATA.unitInfo}
										pagination={false}
										size='small'
										columns={[
											{
												title: 'Tên biến',
												dataIndex: 'variable',
												key: 'variable',
												render: (text: string) => <code className='bg-gray-100 px-2 py-1 rounded'>{text}</code>,
											},
											{
												title: 'Diễn giải',
												dataIndex: 'description',
												key: 'description',
											},
											{
												title: 'Ví dụ',
												dataIndex: 'example',
												key: 'example',
												render: (text: string) => <span className='text-blue-600'>{text}</span>,
											},
										]}
									/>
								),
							},
							{
								key: '3',
								label: 'Thông tin khóa học',
								children: (
									<Table
										dataSource={GUIDE_DATA.courseInfo}
										pagination={false}
										size='small'
										columns={[
											{
												title: 'Tên biến',
												dataIndex: 'variable',
												key: 'variable',
												render: (text: string) => <code className='bg-gray-100 px-2 py-1 rounded'>{text}</code>,
											},
											{
												title: 'Diễn giải',
												dataIndex: 'description',
												key: 'description',
											},
											{
												title: 'Ví dụ',
												dataIndex: 'example',
												key: 'example',
												render: (text: string) => <span className='text-blue-600'>{text}</span>,
											},
										]}
									/>
								),
							},
							{
								key: '4',
								label: 'Thông tin thời gian',
								children: (
									<Table
										dataSource={GUIDE_DATA.dateInfo}
										pagination={false}
										size='small'
										columns={[
											{
												title: 'Tên biến',
												dataIndex: 'variable',
												key: 'variable',
												render: (text: string) => <code className='bg-gray-100 px-2 py-1 rounded'>{text}</code>,
											},
											{
												title: 'Diễn giải',
												dataIndex: 'description',
												key: 'description',
											},
											{
												title: 'Ví dụ',
												dataIndex: 'example',
												key: 'example',
												render: (text: string) => <span className='text-blue-600'>{text}</span>,
											},
										]}
									/>
								),
							},
							{
								key: '5',
								label: 'Thông tin quyết định',
								children: (
									<Table
										dataSource={GUIDE_DATA.decisionInfo}
										pagination={false}
										size='small'
										columns={[
											{
												title: 'Tên biến',
												dataIndex: 'variable',
												key: 'variable',
												render: (text: string) => <code className='bg-gray-100 px-2 py-1 rounded'>{text}</code>,
											},
											{
												title: 'Diễn giải',
												dataIndex: 'description',
												key: 'description',
											},
											{
												title: 'Ví dụ',
												dataIndex: 'example',
												key: 'example',
												render: (text: string) => <span className='text-blue-600'>{text}</span>,
											},
										]}
									/>
								),
							},
						]}
					/>
				</div>
			</Modal>

			<ErrorModal
				isOpen={errorModalOpen}
				message={errorMessage}
				onClose={() => setErrorModalOpen(false)}
			/>
		</div>
	);
}